import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import api, { bindSessionIdentity } from "@/lib/api";
import { clearCache } from "@/lib/cache";

const AuthContext = createContext(null);
const UID_KEY = "dv_session_uid";
// Per-account browser state that must never survive an account switch.
const USER_SCOPED_KEYS = ["dv_workspace", "duru_assistant_sid", "dv_pricing_country"];

function resetUserScopedState() {
  clearCache();
  USER_SCOPED_KEYS.forEach((k) => localStorage.removeItem(k));
  try { sessionStorage.clear(); } catch {}
}

export function AuthProvider({ children }) {
  const [user, setUserState] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastUid = useRef(null);

  // Single entry point for auth state; wipes cached data whenever the account changes.
  const setUser = useCallback((next) => {
    const uid = next && next.user_id ? next.user_id : null;
    if (uid !== lastUid.current) {
      if (lastUid.current || !uid) resetUserScopedState();
      lastUid.current = uid;
      if (uid) localStorage.setItem(UID_KEY, uid); else localStorage.removeItem(UID_KEY);
    }
    setUserState(next);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (error) {
      if (error?.response?.status && error.response.status !== 401) {
        console.error("Auth check failed:", error);
      }
      setUser(false);
      return null;
    }
  }, [setUser]);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  // Session identity guard: the browser cookie now belongs to another account (or expired).
  useEffect(() => {
    const uid = user && user.user_id ? user.user_id : null;
    bindSessionIdentity(uid, () => {
      resetUserScopedState();
      window.location.replace(window.location.pathname.startsWith("/dashboard") ? "/dashboard" : "/login");
    });
  }, [user]);

  // Cross-tab sync: another tab signed in/out → this tab must not keep showing the old account.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== UID_KEY) return;
      const mine = lastUid.current;
      if ((e.newValue || null) !== (mine || null)) {
        resetUserScopedState();
        window.location.replace(e.newValue ? "/dashboard" : "/login");
      }
    };
    const onFocus = () => { if (lastUid.current) refresh(); };
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", onFocus);
    return () => { window.removeEventListener("storage", onStorage); window.removeEventListener("focus", onFocus); };
  }, [refresh]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (error) { console.error("Logout failed:", error); }
    setUser(false);
    window.location.href = "/";
  }, [setUser]);

  const value = useMemo(() => ({ user, setUser, loading, refresh, logout }), [user, setUser, loading, refresh, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
